/**
 * Account menu — the profile row at the bottom of the sidebar. Click opens an
 * upward popover that hosts (in order): the current workspace + a workspace
 * switcher (only when the user belongs to >1 workspace), account links, and
 * log out. Workspace switching moved here from a dedicated sidebar pill so the
 * sidebar stays clean for single-org users (the common case).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'urql';
import { Q_ME } from '@/lib/queries';
import { useAuth } from '@/auth/AuthContext';
import { useWorkspace } from '@/stores/workspace.store';

const BADGE_COLORS = ['bg-violet-500', 'bg-amber-500', 'bg-emerald-500', 'bg-pink-500', 'bg-blue-500', 'bg-stone-700'];
const SITE_URL = (import.meta.env.VITE_SITE_URL ?? (import.meta.env.DEV ? 'http://localhost:5181' : '')).replace(/\/$/, '');

export function ProfileMenu({ collapsed }: { collapsed: boolean }) {
  const nav = useNavigate();
  const { user, signOut } = useAuth();
  const { credits } = useWorkspace();
  const [{ data }] = useQuery({ query: Q_ME });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const workspaces: any[] = data?.me?.workspaces ?? [];
  const currentId = data?.me?.currentWorkspaceId ?? localStorage.getItem('workspace_id');
  const currentWs = workspaces.find((w) => w.id === currentId) ?? workspaces[0];

  const name = user?.displayName ?? user?.email ?? data?.me?.user?.name ?? data?.me?.user?.email ?? 'Account';
  const initial = (name?.[0] ?? 'U').toUpperCase();

  const switchWorkspace = (id: string) => {
    if (id === currentId) { setOpen(false); return; }
    localStorage.setItem('workspace_id', id);
    location.reload();   // simplest cache flush so workspace-scoped queries refetch
  };

  const logout = async () => {
    await signOut();
    localStorage.removeItem('access_token');
    localStorage.removeItem('workspace_id');
    location.href = SITE_URL || '/';
  };

  const go = (path: string) => { setOpen(false); nav(path); };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} type="button" aria-haspopup="menu" aria-expanded={open}
        className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-stone-50 ${collapsed ? 'justify-center' : ''}`}>
        <span className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">{initial}</span>
        {!collapsed && (
          <>
            <span className="flex-1 min-w-0 text-left">
              <span className="block text-sm font-medium text-stone-900 truncate">{name}</span>
              <span className="block text-[10px] text-stone-400">⚡ {credits} credits</span>
            </span>
            <span className="text-stone-400 text-xs">▴</span>
          </>
        )}
      </button>

      {open && (
        <div role="menu"
          className={`absolute z-50 bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden ${collapsed ? 'left-14 bottom-0 w-60' : 'left-0 right-0 bottom-full mb-1'}`}>
          {/* Current workspace + switcher (list only when >1) */}
          {currentWs && (
            <div className="px-3 pt-2 pb-1">
              <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Workspace</p>
              {workspaces.length > 1 ? (
                <div className="mt-1 space-y-0.5">
                  {workspaces.map((w, i) => {
                    const on = w.id === currentId;
                    return (
                      <button key={w.id} onClick={() => switchWorkspace(w.id)} type="button" role="menuitem"
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left hover:bg-stone-50 ${on ? 'bg-violet-50' : ''}`}>
                        <span className={`w-6 h-6 rounded ${BADGE_COLORS[i % BADGE_COLORS.length]} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>{w.name?.[0]?.toUpperCase() ?? 'W'}</span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium text-stone-900 truncate">{w.name}</span>
                          <span className="block text-[10px] text-stone-400 capitalize">{w.plan} · {w.role}</span>
                        </span>
                        {on && <span className="text-violet-600 text-xs">✓</span>}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="mt-1 flex items-center gap-2 px-2 py-1.5">
                  <span className={`w-6 h-6 rounded ${BADGE_COLORS[0]} text-white flex items-center justify-center text-xs font-bold flex-shrink-0`}>{currentWs.name?.[0]?.toUpperCase() ?? 'W'}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-stone-900 truncate">{currentWs.name}</span>
                    <span className="block text-[10px] text-stone-400 capitalize">{currentWs.plan} · {currentWs.role}</span>
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="border-t border-stone-100 py-1">
            {[
              { label: 'Profile', path: '/app/profile', icon: '◔' },
              { label: 'Settings', path: '/app/settings', icon: '⚙' },
              { label: 'Billing', path: '/app/billing', icon: '◊' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} type="button" role="menuitem"
                className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-50">
                <span className="w-4 text-center text-stone-400">{item.icon}</span>{item.label}
              </button>
            ))}
          </div>

          <div className="border-t border-stone-100 py-1">
            <button onClick={logout} type="button" role="menuitem"
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">
              <span className="w-4 text-center">⤴</span>Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
